import { type DepositBookingRow } from '@utazzo/shared';
import { UzDatePicker, UzInput, UzSelect } from '@utazzo/ui';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateDMY, formatInrPaise, isoToLocalDate, toDateInputIso } from '../invoices.helpers';
import { useDepositBooking, useInvoiceAgents } from '../use-invoices';

import {
  ReportListScreen,
  type ReportColumn,
} from '@/components/report-list-screen/ReportListScreen';

/**
 * Deposit Booking Report - 1:1 port of depositbookingreport.aspx: bookings
 * created in the window that still owe a balance, with the full booking detail
 * and money totals. Built on the shared ReportListScreen.
 */

const rupees = (paise: string): number => Number(paise) / 100;
const sumPaise = (rows: ReadonlyArray<DepositBookingRow>, pick: (r: DepositBookingRow) => string) =>
  formatInrPaise(rows.reduce((acc, r) => acc + BigInt(pick(r)), 0n).toString());

const money = (
  header: string,
  pick: (r: DepositBookingRow) => string,
): ReportColumn<DepositBookingRow> => ({
  header,
  align: 'right',
  value: (r) => rupees(pick(r)),
  render: (r) => formatInrPaise(pick(r)),
  total: (rows) => sumPaise(rows, pick),
});

export const DepositBookingPage = (): React.JSX.Element => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [agent, setAgent] = useState('');

  const query = {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(bookingId.trim() ? { bookingId: bookingId.trim() } : {}),
    ...(customerName.trim() ? { customerName: customerName.trim() } : {}),
    ...(email.trim() ? { email: email.trim() } : {}),
    ...(amount.trim() ? { amount: Number(amount) } : {}),
    ...(agent ? { agent } : {}),
  };
  const reportQuery = useDepositBooking(query);
  const rows = reportQuery.data?.rows ?? [];

  const agentsQuery = useInvoiceAgents();
  const agentOptions = [
    { value: '', label: 'All agents' },
    ...(agentsQuery.data ?? []).map((a) => ({ value: a, label: a })),
  ];

  const columns: ReportColumn<DepositBookingRow>[] = [
    {
      header: 'Proforma No.',
      value: (r) => r.proformaNumber ?? '',
      render: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="text-primary hover:underline">
          {r.proformaNumber ?? '-'}
        </Link>
      ),
    },
    { header: 'Created', value: (r) => (r.createdDate ? formatDateDMY(r.createdDate) : '-') },
    { header: 'Customer', value: (r) => r.customerName ?? '' },
    { header: 'Email', value: (r) => r.email ?? '' },
    { header: 'Mobile', value: (r) => r.mobile ?? '' },
    money('Gross Sale', (r) => r.grossSalePaise),
    money('Supplier', (r) => r.supplierCostPaise),
    money('Profit', (r) => r.profitPaise),
    money('Received', (r) => r.receivedPaise),
    money('Balance', (r) => r.balancePaise),
    money('GST', (r) => r.gstPaise),
    { header: 'Destination', value: (r) => r.destination ?? '' },
    { header: 'Check In', value: (r) => (r.checkInDate ? formatDateDMY(r.checkInDate) : '-') },
    { header: 'Check Out', value: (r) => (r.checkOutDate ? formatDateDMY(r.checkOutDate) : '-') },
    { header: 'Product', value: (r) => r.productType ?? '' },
    { header: 'Agent', value: (r) => r.agentName ?? '' },
    { header: 'Branch', value: (r) => r.branchName ?? '' },
    { header: 'Source', value: (r) => r.querySource ?? '' },
  ];

  return (
    <ReportListScreen
      title="Deposit Booking Report"
      description="Bookings created in the period that still owe a balance, with full booking detail."
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.invoiceId}
      loading={reportQuery.isLoading}
      error={reportQuery.isError}
      emptyMessage="No bookings with a balance match these filters."
      exportName="deposit-booking"
      filters={
        <>
          <UzDatePicker
            label="Created from"
            value={isoToLocalDate(from)}
            onValueChange={(d) => setFrom(d ? toDateInputIso(d) : '')}
            containerClassName="w-40"
          />
          <UzDatePicker
            label="Created to"
            value={isoToLocalDate(to)}
            onValueChange={(d) => setTo(d ? toDateInputIso(d) : '')}
            containerClassName="w-40"
          />
          <UzInput
            label="Proforma No."
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            containerClassName="w-40"
          />
          <UzInput
            label="Customer"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            containerClassName="w-44"
          />
          <UzInput
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            containerClassName="w-44"
          />
          <UzInput
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            containerClassName="w-36"
          />
          <UzSelect
            label="Agent"
            value={agent}
            onValueChange={setAgent}
            options={agentOptions}
            containerClassName="w-44"
          />
        </>
      }
    />
  );
};
